import React, { ChangeEvent, FC, useEffect } from "react";
import { Menu, Textarea } from "@evergis/ui";
import { useTranslation } from "react-i18next";

import { IMenuProps } from "@evergis/ui/dist/organisms/Menu/types";
import { useValidationServerError } from "../../../../hooks/useValidationServerError";
import {
  AdditionalInfoLink,
  AdvancedActionButton,
  AdvancedFunctionButton,
  ButtonBlock,
  ButtonRow,
  PopupMenu,
  MenuListWrapper,
} from "../styled";

import { useAdvancedEditor } from "../hooks/useAdvancedEditor";
import { useLayerAttributeOptions } from "../../../../hooks/attributes/useLayerAttributeOptions";
import { ServerValidationError } from "./ServerValidationError";

type AdvancedEditorProps = {
  layerName: string;
  editorExpression?: string | number;
  handleSetExpression: (value: string) => void;
  onChangeValue: (event: ChangeEvent<HTMLTextAreaElement>) => void;
  disabled?: boolean;
};

// eslint-disable-next-line max-lines-per-function
export const AdvancedEditor: FC<AdvancedEditorProps> = ({
  layerName,
  handleSetExpression,
  editorExpression,
  onChangeValue,
  disabled,
}) => {
  const { t } = useTranslation("dialogs");
  const attributeOptions = useLayerAttributeOptions(layerName);
  const { error, setError } = useValidationServerError();

  const { attributes, dateFunctions, handlers, refElement } = useAdvancedEditor(
    handleSetExpression,
    editorExpression,
    attributeOptions,
    onChangeValue,
  );
  const { selectOption, printSymbol, printNullValue, printDate, printOperator, onChangeEditorValue } = handlers;

  useEffect(() => {
    setError(null);
  }, []);

  return (
    <>
      <Textarea
        innerRef={refElement}
        label={t("featuresMultiEdit.value")}
        value={editorExpression}
        onChange={e => {
          error && setError(null);
          onChangeEditorValue(e);
        }}
        style={{ height: "6.625rem" }}
        error={!!error}
        help={error ? <ServerValidationError error={error} /> : ""}
        disabled={disabled}
      />
      <ButtonRow>
        <ButtonBlock>
          <AdvancedFunctionButton width={"4.5rem"} onClick={printSymbol("''")} disabled={disabled}>
            {t("featuresMultiEdit.text")}
          </AdvancedFunctionButton>
          <AdvancedFunctionButton width={"3.4375rem"} onClick={printDate} disabled={disabled}>
            {t("featuresMultiEdit.date")}
          </AdvancedFunctionButton>
          <PopupMenu
            isVisible={dateFunctions.isvisibleDateFunctions}
            onRequestClose={dateFunctions.toggleDateFunctions}
            placement={"bottom-start"}
            anchor={ref => (
              <AdvancedFunctionButton
                style={{ fontSize: "0.5rem", marginLeft: "-0.4rem" }}
                ref={ref}
                onClick={dateFunctions.toggleDateFunctions}
                width={"1rem"}
                disabled={disabled}
              >
                &#x25BC;
              </AdvancedFunctionButton>
            )}
          >
            <Menu
              options={[
                {
                  text: t("featuresMultiEdit.now"),
                  value: "{now}",
                  description: t("featuresMultiEdit.nowDescription"),
                },
                {
                  text: t("featuresMultiEdit.today"),
                  value: "{today}",
                  description: t("featuresMultiEdit.todayDescription"),
                },
                {
                  text: t("featuresMultiEdit.weekStart"),
                  value: "{this_week}",
                  description: t("featuresMultiEdit.weekStartDescription"),
                },
                {
                  text: t("featuresMultiEdit.monthStart"),
                  value: "{this_month}",
                  description: t("featuresMultiEdit.monthStartDescription"),
                },
                {
                  text: t("featuresMultiEdit.yearStart"),
                  value: "{this_year}",
                  description: t("featuresMultiEdit.yearStartDescription"),
                },
              ]}
              selectOption={selectOption("dateFunctions") as IMenuProps["selectOption"]}
            />
          </PopupMenu>
          <PopupMenu
            isVisible={attributes.isvisibleAttributes}
            onRequestClose={attributes.toggleAttributes}
            placement={"bottom-start"}
            anchor={ref => (
              <AdvancedFunctionButton
                ref={ref}
                onClick={attributes.toggleAttributes}
                width={"7rem"}
                disabled={disabled}
              >
                {t("featuresMultiEdit.attribute")}
              </AdvancedFunctionButton>
            )}
          >
            <MenuListWrapper>
              <Menu
                options={attributeOptions}
                selectOption={selectOption("attributes") as IMenuProps["selectOption"]}
              />
            </MenuListWrapper>
          </PopupMenu>
          <AdvancedFunctionButton width={"4.5rem"} onClick={printNullValue} disabled={disabled}>
            null
          </AdvancedFunctionButton>
        </ButtonBlock>
        <ButtonBlock>
          <AdvancedActionButton disabled={!editorExpression} kind={"plus"} onClick={printOperator("+")} />
          <AdvancedActionButton disabled={!editorExpression} kind={"minus"} onClick={printOperator("-")} />
          <AdvancedActionButton disabled={!editorExpression} kind={"close"} onClick={printOperator("*")} />
          <AdvancedActionButton disabled={!editorExpression} kind={"divide"} onClick={printOperator("/")} />
          <AdvancedFunctionButton disabled={!editorExpression} width={"2rem"} onClick={printSymbol("()")}>
            ( )
          </AdvancedFunctionButton>
        </ButtonBlock>
      </ButtonRow>
      <AdditionalInfoLink href={"/external/query-language"}>
        {t("featuresMultiEdit.eqlAdditionalInfo")}
      </AdditionalInfoLink>
    </>
  );
};
